/**
 * Composant de rapport post-génération des emplois du temps.
 * Affiche un rapport détaillé avec alertes critiques pour les matières non placées.
 */

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp,
  Calendar, ArrowRight, BookOpen, X, ArrowLeftRight, Users, Building2, ShieldCheck, ShieldAlert, UserX,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import type { GenerationResultat, Suggestion, VerificationJoursEmpruntesResult } from '../../services/emploisTempsApi';
import { useLazyVerifierJoursEmpruntesQuery } from '../../services/emploisTempsApi';

interface Props {
  result: GenerationResultat;
  sessionId?: number;
  onExtendPeriod?: (newDateFin: string) => void;
  onDismiss?: () => void;
}

const RapportGeneration: React.FC<Props> = ({ result, sessionId, onExtendPeriod, onDismiss }) => {
  const isDark = useSelector((state: RootState) => state.ui.theme) === 'dark';
  const [expandedNiveaux, setExpandedNiveaux] = useState<Set<string>>(new Set());
  const [acknowledged, setAcknowledged] = useState(false);
  const [triggerVerifEmprunts, { data: verifEmprunts, isLoading: verifLoading }] = useLazyVerifierJoursEmpruntesQuery();

  // Auto-vérifier les jours empruntés si présents
  useEffect(() => {
    if (result.jours_empruntes && result.jours_empruntes.nombre > 0 && sessionId) {
      triggerVerifEmprunts(sessionId);
    }
  }, [result.jours_empruntes, sessionId, triggerVerifEmprunts]);

  const rapport = result.rapport;
  if (!rapport) return null;

  const isCritique = rapport.criticite === 'critique';

  const toggleNiveau = (niveau: string) => {
    setExpandedNiveaux(prev => {
      const next = new Set(prev);
      if (next.has(niveau)) next.delete(niveau);
      else next.add(niveau);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* Bannière critique */}
      {isCritique && !acknowledged && (
        <div className={`rounded-xl border-2 p-5 ${
          isDark ? 'bg-red-950/40 border-red-600' : 'bg-red-50 border-red-400'
        }`}>
          <div className="flex items-start gap-3">
            <XCircle size={28} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className={`text-lg font-bold ${isDark ? 'text-red-300' : 'text-red-800'}`}>
                Attention : {rapport.total_non_placees} matiere(s) non placee(s)
              </h3>
              <p className={`mt-1 ${isDark ? 'text-red-200' : 'text-red-700'}`}>
                L'emploi du temps genere est incomplet. Certaines matieres n'ont pas pu etre
                placees par manque de creneaux disponibles. Ne publiez pas cet emploi du temps
                en l'etat.
              </p>

              {/* Matières manquantes par niveau */}
              <div className="mt-3 space-y-2">
                {Object.entries(result.resultats).map(([niveau, data]) => {
                  if (data.matieres_non_placees === 0) return null;
                  return (
                    <div key={niveau} className={`rounded-lg p-3 ${
                      isDark ? 'bg-red-900/30' : 'bg-red-100'
                    }`}>
                      <button
                        onClick={() => toggleNiveau(niveau)}
                        className="w-full flex items-center justify-between"
                      >
                        <span className="font-semibold">
                          {niveau} : {data.matieres_non_placees} matiere(s) manquante(s)
                          sur {data.matieres_total}
                        </span>
                        {expandedNiveaux.has(niveau) ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                      {expandedNiveaux.has(niveau) && data.detail_non_placees && (
                        <ul className="mt-2 space-y-1">
                          {data.detail_non_placees.map((m, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm">
                              <BookOpen size={14} className="text-red-400" />
                              <span className="font-medium">{m.nom}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                isDark ? 'bg-red-800 text-red-200' : 'bg-red-200 text-red-700'
                              }`}>
                                {m.option_codes.join(', ')}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Suggestions */}
              {rapport.suggestions.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className={`font-semibold ${isDark ? 'text-red-200' : 'text-red-700'}`}>
                    Solutions recommandees :
                  </h4>
                  {rapport.suggestions
                    .filter(s => s.type === 'etendre_periode' || s.type === 'activer_samedi' || s.type === 'emprunter_jour')
                    .map((suggestion, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${
                      isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-red-200'
                    }`}>
                      <Calendar size={18} className="text-blue-500 flex-shrink-0" />
                      <span className="flex-1 text-sm">{suggestion.message}</span>
                      {suggestion.type === 'etendre_periode' && suggestion.nouvelle_date_fin && onExtendPeriod && (
                        <button
                          onClick={() => onExtendPeriod(suggestion.nouvelle_date_fin!)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                        >
                          Appliquer <ArrowRight size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Bouton acquitter */}
              <button
                onClick={() => setAcknowledged(true)}
                className={`mt-4 px-4 py-2 rounded-lg text-sm font-medium ${
                  isDark
                    ? 'bg-red-800 text-red-100 hover:bg-red-700'
                    : 'bg-red-200 text-red-800 hover:bg-red-300'
                } transition`}
              >
                J'ai pris connaissance du probleme
              </button>
            </div>
            {onDismiss && (
              <button onClick={onDismiss} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Rapport résumé (toujours visible) */}
      <div className={`rounded-xl border p-5 ${
        isCritique
          ? isDark ? 'bg-amber-950/20 border-amber-700' : 'bg-amber-50 border-amber-200'
          : isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'
      }`}>
        <div className="flex items-center gap-2 mb-4">
          {isCritique ? (
            <AlertTriangle size={20} className="text-amber-500" />
          ) : (
            <CheckCircle size={20} className="text-green-500" />
          )}
          <h3 className="font-semibold">
            {isCritique ? 'Rapport de generation (incomplet)' : 'Generation reussie'}
          </h3>
          {onDismiss && !isCritique && (
            <button onClick={onDismiss} className="ml-auto text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          )}
        </div>

        {/* Stats globales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard
            label="Creneaux crees"
            value={result.total_creneaux}
            color="blue"
            isDark={isDark}
          />
          <StatCard
            label="Jours utilises"
            value={result.jours_utilises}
            color="green"
            isDark={isDark}
          />
          <StatCard
            label="Matieres placees"
            value={`${rapport.total_placees}/${rapport.total_matieres}`}
            color={isCritique ? 'amber' : 'green'}
            isDark={isDark}
          />
          <StatCard
            label="Taux placement"
            value={`${rapport.taux_placement_global}%`}
            color={rapport.taux_placement_global === 100 ? 'green' : 'red'}
            isDark={isDark}
          />
        </div>

        {/* Tableau par niveau */}
        <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="w-full text-sm">
            <thead className={isDark ? 'bg-gray-800' : 'bg-gray-100'}>
              <tr>
                <th className="text-left px-3 py-2">Niveau</th>
                <th className="text-center px-3 py-2">Groupes</th>
                <th className="text-center px-3 py-2">Total</th>
                <th className="text-center px-3 py-2">Placees</th>
                <th className="text-center px-3 py-2">Manquantes</th>
                <th className="text-center px-3 py-2">Taux</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(result.resultats).map(([niveau, data]) => (
                <tr
                  key={niveau}
                  className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} ${
                    data.matieres_non_placees > 0
                      ? isDark ? 'bg-red-900/10' : 'bg-red-50'
                      : ''
                  }`}
                >
                  <td className="px-3 py-2 font-medium">{niveau}</td>
                  <td className="text-center px-3 py-2">{data.groupes}</td>
                  <td className="text-center px-3 py-2">{data.matieres_total}</td>
                  <td className="text-center px-3 py-2 text-green-600">{data.matieres_placees}</td>
                  <td className={`text-center px-3 py-2 font-bold ${
                    data.matieres_non_placees > 0 ? 'text-red-500' : 'text-gray-400'
                  }`}>
                    {data.matieres_non_placees}
                  </td>
                  <td className="text-center px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      data.taux_placement === 100
                        ? isDark ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-700'
                        : isDark ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-700'
                    }`}>
                      {data.taux_placement}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Avertissements textuels */}
        {result.avertissements.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-yellow-600 mb-1">Avertissements :</h4>
            <ul className="list-disc list-inside text-sm text-yellow-500 space-y-0.5">
              {result.avertissements.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* ═══ Enseignants manquants post-génération ═══ */}
      {result.enseignants_manquants && result.enseignants_manquants.length > 0 && (
        <div className={`rounded-xl border p-5 ${
          isDark ? 'bg-amber-950/20 border-amber-700' : 'bg-amber-50 border-amber-200'
        }`}>
          <div className="flex items-center gap-2 mb-3">
            <UserX size={20} className="text-amber-500" />
            <h3 className="font-semibold">
              {result.enseignants_manquants.length} matiere(s) sans formateur
            </h3>
          </div>
          <p className={`text-sm mb-3 ${isDark ? 'text-amber-200' : 'text-amber-700'}`}>
            Ces matieres ont ete placees mais n'ont aucun formateur attribue.
            Attribuez les formateurs avant de publier l'emploi du temps.
          </p>
          <div className="flex flex-wrap gap-2">
            {result.enseignants_manquants.map((e, i) => (
              <span key={i} className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                isDark ? 'bg-amber-900/40 text-amber-300 border border-amber-700' : 'bg-amber-100 text-amber-800'
              }`}>
                <span className="font-medium">{e.niveau}</span>
                <span>—</span>
                <span>{e.matiere}</span>
                <span className="opacity-60">({e.groupe})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ═══ Calendrier jour par jour ═══ */}
      {result.calendrier && result.calendrier.length > 0 && (
        <div className={`rounded-xl border p-5 ${
          isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <button
            onClick={() => toggleNiveau('__calendrier__')}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Calendar size={20} className="text-blue-500" />
              <h3 className="font-semibold">Calendrier ({result.calendrier.length} jours)</h3>
            </div>
            {expandedNiveaux.has('__calendrier__') ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {expandedNiveaux.has('__calendrier__') && (
            <div className={`mt-3 rounded-lg overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <table className="w-full text-xs">
                <thead className={isDark ? 'bg-gray-800' : 'bg-gray-100'}>
                  <tr>
                    <th className="text-left px-3 py-2">Jour</th>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-center px-3 py-2">Niveaux</th>
                    <th className="text-center px-3 py-2">Matieres</th>
                  </tr>
                </thead>
                <tbody>
                  {result.calendrier.map((j, i) => (
                    <tr key={i} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <td className="px-3 py-2 font-medium">{j.jour_semaine}</td>
                      <td className="px-3 py-2">{j.date_label}</td>
                      <td className="text-center px-3 py-2">
                        <div className="flex justify-center gap-1">
                          {j.niveaux.map(n => (
                            <span key={n} className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              n === 'L1'
                                ? isDark ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                                : isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                            }`}>{n}</span>
                          ))}
                        </div>
                      </td>
                      <td className="text-center px-3 py-2">{j.nb_matieres}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Section Jours Empruntés */}
      {result.jours_empruntes && result.jours_empruntes.nombre > 0 && (
        <div className={`rounded-xl border-2 p-5 ${
          isDark ? 'bg-purple-950/30 border-purple-700' : 'bg-purple-50 border-purple-300'
        }`}>
          <div className="flex items-center gap-2 mb-4">
            <ArrowLeftRight size={20} className="text-purple-500" />
            <h3 className="font-semibold">
              {result.jours_empruntes.nombre} jour(s) emprunte(s)
            </h3>
          </div>

          {/* Détail des jours empruntés */}
          <div className="space-y-3">
            {result.jours_empruntes.details.map((jour, i) => (
              <div key={i} className={`p-3 rounded-lg ${
                isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-purple-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-purple-500" />
                    <span className="font-semibold">{jour.jour_semaine} {jour.date_label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isDark ? 'bg-purple-800 text-purple-200' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {jour.source} → {jour.cible}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">{jour.nb_matieres} matiere(s)</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {jour.matieres.map((m, j) => (
                    <span key={j} className={`text-xs px-2 py-0.5 rounded ${
                      isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                    }`}>{m}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Vérification Brassage */}
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Building2 size={18} className="text-purple-400" />
              <h4 className="font-semibold text-sm">Verification brassage</h4>
            </div>

            {verifLoading ? (
              <p className="text-sm text-gray-500">Verification en cours...</p>
            ) : verifEmprunts ? (
              <div className="space-y-3">
                {/* Alertes */}
                {verifEmprunts.alertes.map((alerte, i) => (
                  <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                    alerte.type === 'error'
                      ? isDark ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200'
                      : isDark ? 'bg-amber-900/30 border border-amber-700' : 'bg-amber-50 border border-amber-200'
                  }`}>
                    <ShieldAlert size={18} className={alerte.type === 'error' ? 'text-red-500' : 'text-amber-500'} />
                    <div>
                      <p className="font-semibold">{alerte.titre}</p>
                      <p className="mt-0.5">{alerte.message}</p>
                    </div>
                  </div>
                ))}

                {/* Détail par jour emprunté */}
                {verifEmprunts.jours_empruntes.map((jour, i) => (
                  <div key={i} className={`p-3 rounded-lg ${
                    jour.statut === 'ok'
                      ? isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                      : jour.statut === 'erreur'
                        ? isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'
                        : isDark ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      {jour.statut === 'ok' ? (
                        <ShieldCheck size={16} className="text-green-500" />
                      ) : jour.statut === 'erreur' ? (
                        <ShieldAlert size={16} className="text-red-500" />
                      ) : (
                        <AlertTriangle size={16} className="text-amber-500" />
                      )}
                      <span className="text-sm font-medium">{jour.jour_semaine} {jour.date_label}</span>
                      {jour.etudiants_concernes !== undefined && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Users size={12} /> {jour.etudiants_concernes} etudiant(s)
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 ml-6">{jour.message}</p>

                    {/* Tableau des salles */}
                    {jour.salles_habituelles && jour.salles_habituelles.length > 0 && (
                      <div className="mt-2 ml-6">
                        <details className="text-xs">
                          <summary className="cursor-pointer text-purple-500 hover:text-purple-400">
                            Voir {jour.salles_habituelles.length} salle(s) assignee(s)
                          </summary>
                          <div className={`mt-1 rounded overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <table className="w-full text-xs">
                              <thead className={isDark ? 'bg-gray-800' : 'bg-gray-100'}>
                                <tr>
                                  <th className="text-left px-2 py-1">Site</th>
                                  <th className="text-left px-2 py-1">Salle</th>
                                  <th className="text-center px-2 py-1">Effectif</th>
                                  <th className="text-center px-2 py-1">Capacite</th>
                                </tr>
                              </thead>
                              <tbody>
                                {jour.salles_habituelles.map((s, j) => (
                                  <tr key={j} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <td className="px-2 py-1">{s.site}</td>
                                    <td className="px-2 py-1">{s.salle}</td>
                                    <td className="text-center px-2 py-1">{s.effectif}</td>
                                    <td className="text-center px-2 py-1">{s.capacite}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                ))}

                {/* Conseil */}
                {verifEmprunts.conseil && verifEmprunts.alertes.length === 0 && (
                  <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                    isDark ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
                  }`}>
                    <CheckCircle size={16} className="text-blue-500 mt-0.5" />
                    <p>{verifEmprunts.conseil}</p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string | number;
  color: string;
  isDark: boolean;
}> = ({ label, value, color, isDark }) => {
  const colorClasses: Record<string, string> = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    amber: 'text-amber-500',
    red: 'text-red-500',
  };
  return (
    <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
      <div className={`text-2xl font-bold ${colorClasses[color] || 'text-gray-500'}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
};

export default RapportGeneration;
